import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Appointment } from '../v2/services/Appointment';

export default function SearchDoctorResults() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchParams, setSearchParams] = useState({});

    useEffect(() => {
        // Get search parameters from location state
        if (location.state?.searchParams) {
            setSearchParams(location.state.searchParams);
            performSearch(location.state.searchParams);
        } else {
            setError('No se encontraron parámetros de búsqueda');
            setIsLoading(false);
        }
    }, [location.state]);

    const performSearch = async (params) => {
        setIsLoading(true);
        setError('');

        try {
            const response = await Appointment.searchAppointmentsByUser({
                dui: params.dui || null,
                email: params.email || null
            });

            console.log("response", response);

            if (response.data && response.data?.data?.length > 0) {
                // Flatten the appointments from all users
                const allAppointments = response.data?.data?.reduce((acc, userData) => {
                    return acc.concat(userData.appointments || []);
                }, []);

                if (allAppointments.length > 0) {
                    setSearchResults(allAppointments);
                } else {
                    setError('No se encontraron citas con los datos proporcionados');
                }
            } else {
                setError('No se encontraron citas con los datos proporcionados');
            }
        } catch (err) {
            console.error('Error searching appointments:', err);
            setError('Error al buscar las citas. Inténtelo de nuevo.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewAppointment = (appointment) => {
        // Navigate to appointment detail page with appointment data
        navigate('/doctor-appointment-detail', {
            state: {
                patient: appointment,
                type: 'viewOnly',
                fromSearch: true
            }
        });
    };

    const handleNewSearch = () => {
        navigate('/');
    };

    if (isLoading) {
        return (
            <div className="bg-dl-screen-lightblue py-12 lg:px-8 mx-auto px-4 relative sm:px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        <p className="mt-4 text-gray-600">Buscando citas...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-dl-screen-lightblue py-12 lg:px-8 mx-auto px-4 relative sm:px-6">
            <div className="bg-dl-screen-lightblue min-h-[calc(100vh_-_306px)]">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="relative z-10 flex items-baseline justify-center pt-12 pb-6 border-gray-200">
                        <h1 className="text-4xl font-extrabold tracking-tight text-dl-secondary">
                            Resultados de Búsqueda de Citas
                        </h1>
                    </div>


                    {/* Error Display */}
                    {error !== '' && (
                        <div className="mb-8 bg-red-100 border-t-4 border-red-500 rounded-b text-red-900 px-4 py-3 shadow-md" role="alert">
                            <div className="flex">
                                <div className="py-1">
                                    <svg className="fill-current h-6 w-6 text-red-500 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                        <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="font-bold">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Results */}
                    {searchResults.length > 0 && (
                        <section aria-labelledby="appointments-heading" className="pb-24">
                            <h2 id="appointments-heading" className="sr-only">
                                Citas Encontradas
                            </h2>

                            <div className="filters-list-show-box">
                                {searchResults.map((appointment, index) => (
                                    <div
                                        key={appointment.appointmentID || index}
                                        className="filters-list-show-box-inner bg-white py-6 px-6 flex justify-between mb-4 border-l-8 rounded-l-lg rounded-r-full border-light-blue items-center"
                                    >
                                        <div className="filters-info flex items-center boxfilter-list-comm flex-1">
                                            <figure className="border-4 rounded-full border-light-blue w-20 h-20">
                                                <span className="py-1 px-1 block w-100 h-full">
                                                    <img
                                                        src={appointment.personalImage ? `${process.env.REACT_APP_API_SERVER}/${appointment.personalImage}` : '/defaultIcons/profile.png'}
                                                        className="h-full w-full rounded-full"
                                                        alt=""
                                                        onError={(e) => {
                                                            e.target.src = '/defaultIcons/profile.png';
                                                        }}
                                                    />
                                                </span>
                                            </figure>
                                            <figcaption className="ml-10 flex-1">
                                                <h4 className="text-black text-xl font-bold">{appointment.userFullName || 'Nombre no disponible'}</h4>
                                                <span className="tag-info light-blue py-2 px-3 rounded-full block text-center text-base mt-3 w-44">
                                                    {appointment.motiveName || 'Consulta'}
                                                </span>
                                            </figcaption>
                                        </div>

                                        <div className="boxfilter-list-comm pr-12 pl-12 w-72 m-auto">
                                            <h4 className="text-black text-xl font-bold text-center">Fecha y Hora</h4>
                                            <span className="tag-info light-blue py-2 px-3 rounded-full block text-center text-base mt-3 w-44 m-auto">
                                                {appointment.appDate} - {appointment.appTime}
                                            </span>
                                        </div>

                                        <div className="boxfilter-list-comm pr-12 pl-12 w-80 m-auto">
                                            <div className="availability-box light-blue py-8 px-6 rounded-[20px] text-center">
                                                <h5 className="text-xl font-bold pb-1">Contacto</h5>
                                                <span className="availability-date text-xl font-normal">
                                                    {appointment.userEmail || 'No disponible'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="btn-box pr-6 pl-12">
                                            <button
                                                onClick={() => handleViewAppointment(appointment)}
                                                className="bg-blue text-white py-3 px-6 rounded-full"
                                            >
                                                Ver Detalles
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}
